import {
    ENQUIRY_AUTO_SUBMIT_STORAGE_KEY,
    ENQUIRY_SESSION_STORAGE_KEY,
} from '../constants';

/**
 * Forget the saved enquiry draft and anything primed to send it.
 *
 * The draft holds a name, an email address, a phone number and a suburb, kept
 * so a guest who hits the auth wall mid-enquiry gets their form back on the
 * far side. It is keyed to the tab rather than to a member, which is what it
 * needs to be for that job — and also why logging out left it sitting there:
 * the next person to open the enquiry modal in that tab was shown the last
 * person's contact details, already filled in.
 *
 * Called on logout, where a reader has said in as many words that they are
 * done. Deliberately not the passkey device hint: that names a member on
 * purpose, so this browser can offer them their own sign-in next time, and
 * carries nothing an attacker holding it could use.
 */
export const clearEnquiryDraft = ({
    /**
     * Close the draft for the rest of the page's life, not just empty it.
     *
     * Right for logout, which reloads: the latch covers the window between the
     * clear and the reload, and dies with the page. Wrong for a caller that
     * stays put — switching account inside the gate keeps the same page, so a
     * latch there would silently no-op every save for the rest of the visit,
     * including the draft the new member is about to build.
     */
    close = true,
}: { close?: boolean } = {}): void => {
    if (typeof window === 'undefined') return;
    if (close) draftClosed = true;
    try {
        window.sessionStorage.removeItem(ENQUIRY_SESSION_STORAGE_KEY);
        window.sessionStorage.removeItem(ENQUIRY_AUTO_SUBMIT_STORAGE_KEY);
    } catch {
        // Storage blocked or full — there was nothing readable to clear.
    }
};

/**
 * Has the draft been taken away for good in this page's life?
 *
 * Clearing on logout is a race it cannot win on its own. Logout does not stop
 * the page — it submits, revalidates and only then reloads — and a mounted
 * enquiry form outlives all three steps holding a full copy of the draft in
 * react-hook-form, which no amount of clearing storage or atoms touches. Any
 * save firing in that window writes the contact details straight back, and
 * `isLoggedIn` flipping is itself what triggers one: the BuildHub block is only
 * offered to members, so losing it runs a cleanup that saves the whole form.
 *
 * So the draft is not just removed, it is closed. Every write goes through
 * `saveEnquirySession`, which refuses once this is set, and the flag lives in
 * the module rather than in storage so it dies with the page — the guest who
 * arrives after the reload starts a draft of their own as usual.
 */
export const isEnquiryDraftClosed = (): boolean => draftClosed;

let draftClosed = false;
